import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

/**
 * Copiar-com-confirmação para o código do cupom (F5-6).
 *
 * Existe como hook, e não inline no card, porque a grade renderiza N cupons e
 * cada um precisa do PRÓPRIO estado de "copiado" e do próprio timer. A
 * alternativa era um `copiadoId` na página furado até cada card, ou repetir o
 * `catch` do `toast` N vezes.
 *
 * ⚠️ **O `catch` é obrigatório e não é defensivismo.** `navigator.clipboard`
 * REJEITA em contexto não seguro (http), quando a permissão foi negada e no
 * jsdom dos testes. Sem ele, o clique falha em silêncio e o médico fica olhando
 * um botão que não faz nada. O texto do toast é o mesmo do
 * `CompartilharDialogo`, adaptado: o código está visível ao lado, então o
 * caminho manual segue aberto.
 *
 * `aoCopiar` é chamado DEPOIS da cópia dar certo — é por onde o resgate é
 * registrado. Falha nele não desfaz o feedback visual: o médico já tem o código
 * na área de transferência, e um "não foi possível copiar" seria mentira.
 */
export function useCopiarCodigo(aoCopiar?: () => void): {
  copiado: boolean;
  copiar: (codigo: string) => Promise<void>;
} {
  const [copiado, setCopiado] = useState(false);
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Desmontar o card com o timer pendente chamaria `setCopiado` num componente
  // que não existe mais — trocar de filtro faz exatamente isso.
  useEffect(() => {
    return () => {
      if (temporizador.current) clearTimeout(temporizador.current);
    };
  }, []);

  const copiar = useCallback(
    async (codigo: string) => {
      try {
        await navigator.clipboard.writeText(codigo);
      } catch {
        toast.error('Não foi possível copiar. Selecione o código e copie à mão.');
        return;
      }

      setCopiado(true);
      if (temporizador.current) clearTimeout(temporizador.current);
      temporizador.current = setTimeout(() => setCopiado(false), 2000);

      aoCopiar?.();
    },
    [aoCopiar],
  );

  return { copiado, copiar };
}
